import { useCallback, useRef, useState, useEffect } from 'react';

import { throttle } from 'lodash-es';
import { useLatestValue } from '@parspec/pixel';

type ThrottledFunc<T extends (...args: any) => any> = ReturnType<typeof throttle<T>>;

/*
* To track the dynamic resizing of elements efficiently, pass the refCallback to the component's ref:
* <Component ref={refCallback} />, and retrieve the dimensions from the hook.

* If the component has an internal ref that needs to access the element,
* You can either pass a ref to the useResize hook and access the component like this:
* 
* const componentRef = useRef<HTMLElement>();
* const [dimensions, refCallback] = useResize({ ref: componentRef });
Then in the component, pass the refCallback:
* <Component ref={refCallback} />

* Alternatively, you can compose multiple refs as shown below:
* 
* const [dimensions, refCallback] = useResize();
* const componentRef = useRef();

NOTE: It's important to wrap the composeRefs function with useCallback:
* const composeRefs = useCallback((node) => {
*     componentRef.current = node; // Assign the node to the component's inner ref
*     refCallback(node); // Pass the node to the ref callback simultaneously
* }, [refCallback]);

Then in the component, use the composed function:
* <Component ref={composeRefs} />

* For more information on why using a ref callback is beneficial in these scenarios,
* check out this blog: https://tkdodo.eu/blog/avoiding-use-effect-with-callback-refs
*/

const getDimensions = (entry: ResizeObserverEntry, node: HTMLElement) => {
    // Polyfill where borderBoxSize is undefined(in one of the instances of Safari)
    // This forces browser to reflow and recalculate, so not performant, that's why it's the fallback
    if (!entry.borderBoxSize?.[0]) {
        return { width: node.offsetWidth, height: node.offsetHeight };
    }

    return {
        width: entry.borderBoxSize[0].inlineSize,
        height: entry.borderBoxSize[0].blockSize,
    };
};

interface ResizeParams {
    ref?: React.MutableRefObject<HTMLElement | null | undefined>,
    throttleTimeout?: number;
    getRawResizedEntry?: (entry: ResizeObserverEntry) => void,
};

const useResize = (resizeParams?: ResizeParams) => {
    const {
        ref,
        getRawResizedEntry,
        throttleTimeout = 300,
    } = resizeParams ?? {};

    const getRawResizedEntryRef = useLatestValue(getRawResizedEntry);
    const [dimensions, setDimensions] = useState<null | { width: number; height: number; }>(null);

    const resizer = useRef<null | ResizeObserver>(null);
    const handleResize = useRef<null | ThrottledFunc<(entries: ResizeObserverEntry[]) => void>>(null);

    useEffect(() => () => {
        handleResize.current?.cancel();
        resizer.current?.disconnect();
    }, []);

    const refCallback = useCallback((node: HTMLElement | null) => {
        if (ref) ref.current = node;
        handleResize.current?.cancel(); // cancel any pending calls of prev node
        resizer.current?.disconnect(); // disconnect resizer for previous node to prevent memory leaks

        if (!node) {
            resizer.current = null;
            return;
        }

        handleResize.current = throttle((entries: ResizeObserverEntry[]) => {
            const [entry] = entries;
            if (entry) {
                getRawResizedEntryRef.current?.(entry);
                setDimensions(getDimensions(entry, node));
            }
        }, throttleTimeout);

        resizer.current = new ResizeObserver(handleResize.current);
        resizer.current.observe(node, { box: "border-box" });
    }, [ref, getRawResizedEntryRef, throttleTimeout]);

    return [dimensions, refCallback] as const;
};

export default useResize;